import { useState, useRef } from 'react';
import { VariableSet, PartialVariableSet } from 'api/data/variables';
import { InputType } from 'types/index';
import { Input } from 'components/UI/Inputs/Input';
import { Modal } from 'components/UI/Modal';
import { useTranslation, useVariables, useUpdateVariableSet } from 'hooks/store';
import { useEffectOnce, useCompletedAction } from 'hooks/utils';

interface Props {
	variableSet: VariableSet;
	onClose: () => void;
}

export function UpdateVariableSetModal({ variableSet, onClose }: Props) {
	const { translate } = useTranslation();

	const { setName, setLabel, identifier } = variableSet;

	const [draftSetLabel, setDraftSetLabel] = useState(setLabel);

	const [
		{
			data: { variableSets }
		}
	] = useVariables({ initial: true, lazy: true });

	const [{ loading: updatingVariableSet, error: errorUpdatingVariableSet }, updateVariableSet] =
		useUpdateVariableSet();

	const draftSetLabelTrimmed = draftSetLabel.trim();
	const isSetLabelValid = draftSetLabelTrimmed.length > 0;
	const isSetLabelUnique = !variableSets.some(
		variableSet => variableSet.setLabel === draftSetLabelTrimmed
	);
	const hasChanges = draftSetLabelTrimmed !== setLabel;

	const inputRef = useRef<HTMLInputElement>(null);

	/**
	 * Select all text on-open
	 * improve UX - save user time
	 */
	useEffectOnce(() => inputRef.current?.select());

	// CLOSE MODAL AFTER GROUP WAS UPDATED
	useCompletedAction(updatingVariableSet, errorUpdatingVariableSet, onClose);

	function handleUpdateVariableSet() {
		if (updatingVariableSet || !isSetLabelValid || !isSetLabelUnique || !hasChanges) return;

		const updatedVariableSet: PartialVariableSet = {
			// TODO: MAYBE `identifier` WILL BE MODIFIED HERE AS WELL
			setName,
			setLabel: draftSetLabelTrimmed,
			identifier
		};

		updateVariableSet(updatedVariableSet);
	}

	const title = `${translate(dict => dict.terms.update)} ${setLabel}`;

	const primaryButtonDisabled = !isSetLabelValid || !isSetLabelUnique || !hasChanges;

	return (
		<Modal
			size={s => s.s}
			title={title}
			primary={{
				label: translate(dict => dict.buttons.update),
				loading: updatingVariableSet,
				disabled: primaryButtonDisabled,
				onClick: handleUpdateVariableSet
			}}
			neutral={{
				label: translate(dict => dict.buttons.cancel),
				onClick: onClose
			}}
			onClose={onClose}
			visible
			close
		>
			<Input
				ref={inputRef}
				type={InputType.Text}
				value={draftSetLabel}
				label={translate(dict => dict.variablesPage.createVariableSetModal.label)}
				placeholder={translate(
					dict => dict.variablesPage.createVariableSetModal.placeholder
				)}
				error={
					hasChanges && !isSetLabelUnique
						? translate(dict => dict.terms.errorlabelUnique)
						: undefined
				}
				onChange={e => setDraftSetLabel(e.target.value)}
				onSubmit={handleUpdateVariableSet}
				autoFocus
			/>
		</Modal>
	);
}
